// AddDatabaseModal.jsx

import React from 'react';

// CSS
import './AddDatabaseModal.css';

// Components
import AddDatabaseModalCreateFlow from './AddDatabaseModalCreateFlow';
import AddDatabaseModalFooter from './AddDatabaseModalFooter';
import AddDatabaseModalLandingStep from './AddDatabaseModalLandingStep';
import AddDatabaseModalTemplateStep from './AddDatabaseModalTemplateStep';
import Modal from 'components/modal/Modal';

// Hooks
import useAddDatabaseModalState from './useAddDatabaseModalState';

/**
 * AddDatabaseModal
 *
 * Wizard modal for adding a new database to the workspace. Offers two paths:
 *   1. Choose a Template — search the dictionary and provision a pre-built database.
 *   2. Create New — define a custom database name/icon, then optionally add fields.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Called when the modal is dismissed.
 * @param {string|null} workspaceKey - Active workspace key.
 *
 * @returns {ReactNode} The rendered modal.
 */
const AddDatabaseModal = React.memo(({
    isOpen,
    onClose,
    workspaceKey,
}) => {
    const {
        flowState,
        dictionaryState,
        createTypeState,
        modalActions,
    } = useAddDatabaseModalState({
        isOpen,
        onClose,
        workspaceKey,
    });

    // ----- RENDER ----- //

    return (
        <Modal
            iconKey="database"
            isOpen={isOpen}
            onClose={modalActions.handleClose}
            title={flowState.modalTitle}
            width={480}
            height="auto"
            maxHeight="90vh"
        >
            <div className="add-database-modal-container">
                {flowState.isLandingStep && (
                    <AddDatabaseModalLandingStep
                        onOpenCreateFlow={modalActions.openCreateFlow}
                        onOpenTemplateFlow={modalActions.openTemplateFlow}
                    />
                )}

                {flowState.isTemplateStep && (
                    <AddDatabaseModalTemplateStep dictionaryState={dictionaryState} />
                )}

                {flowState.isCreateFlow && (
                    <AddDatabaseModalCreateFlow
                        flowState={flowState}
                        createTypeState={createTypeState}
                        workspaceKey={workspaceKey}
                    />
                )}

            </div>

            <AddDatabaseModalFooter
                flowState={flowState}
                dictionaryState={dictionaryState}
                createTypeState={createTypeState}
                modalActions={modalActions}
            />

        </Modal>
    );
});

AddDatabaseModal.displayName = 'AddDatabaseModal';

export default AddDatabaseModal;
